import { Button, Dialog } from "../../design-v2";

/*
 * Phase 11B closure corrective: this overlay used to hand-roll its own modal --
 * a `bg-slate-950/50` backdrop from the stock Tailwind palette, an ad-hoc
 * `z-50`, `shadow-xl` and `rounded-3xl` -- all of which bypassed the Design V2
 * tokens and the elevation scale, and would have let any canonical Dialog
 * (`--lz-v2-z-modal`) or the Settings drawer (`--lz-v2-z-backdrop`) paint over
 * it. It now renders through the canonical `Dialog` / `ModalSurface`.
 *
 * Behaviour is deliberately unchanged. In particular this dialog is NOT
 * dismissible: the original had no close button and no backdrop handler, so the
 * only two exits were and remain "Not now" (cancel) and "Yes, replace cycles"
 * (confirm). `dismissible={false}` keeps Escape and backdrop presses inert
 * rather than silently adding a third, untested way to leave the decision.
 *
 * Callbacks, button labels, disabled/busy states, the conflict list and its
 * accessible name are all as they were. The parent still owns when this is
 * mounted (`programFlow.phase === "confirmation"`), so no onboarding state
 * moved here.
 */
export default function OnboardingConflictModal({ conflicts, onCancel, onConfirm, isBusy }) {
  return (
    <Dialog
      open
      dismissible={false}
      showCloseButton={false}
      title="Replace your scheduled program?"
      description={
        "You already have a training cycle scheduled during this period. Continuing will " +
        "remove the conflicting cycle(s) and replace them with your new 6-week program. Are " +
        "you sure you want to continue?"
      }
      className="lz-onboarding-conflict"
      footer={
        <>
          <Button variant="secondary" className="flex-1" onClick={onCancel} disabled={isBusy}>
            Not now
          </Button>
          <Button className="flex-1" onClick={onConfirm} disabled={isBusy} isLoading={isBusy}>
            Yes, replace cycles
          </Button>
        </>
      }
    >
      {Array.isArray(conflicts) && conflicts.length > 0 ? (
        <ul
          className="lz-onboarding-conflict__list"
          aria-label="Conflicting training cycles"
        >
          {conflicts.map((conflict) => (
            <li key={conflict.cycleId} className="lz-onboarding-conflict__item">
              <span className="lz-onboarding-conflict__name">{conflict.name}</span>
              <span className="lz-onboarding-conflict__range">
                {conflict.startDate} – {conflict.endDate}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </Dialog>
  );
}
